import { useCallback, useEffect, useState } from 'react'
import { FaBell, FaShieldAlt, FaSyncAlt, FaUser } from 'react-icons/fa'
import { useLocation, useNavigate } from 'react-router-dom'
import api from '../../../helpers/apiRequester'
import { useAuthUserData } from '../../../authUserProvider'
import { Button, Card, EmptyState, KeyValueList, Page, PageHeader, StatusPill, Tabs } from 'ui'
import { apiErrorMessage, BreakableEmail, formatDate, formatDateTime, UserProfile } from './shared'
import { PersonalDetailsCard } from './personalDetailsCard'
import { EmailCard } from './emailCard'
import { PasswordCard } from './passwordCard'
import { TwoFactorCard } from './twoFactorCard'
import { SessionsCard } from './sessionsCard'
import { NotificationsCard } from './notificationsCard'
import s from './styles.module.scss'

type ProfileTab = 'profile' | 'security' | 'notifications'
const TABS: ProfileTab[] = ['profile', 'security', 'notifications']

const tabFromHash = (hash: string): ProfileTab => {
    const value = hash.replace(/^#/, '') as ProfileTab
    return TABS.includes(value) ? value : 'profile'
}

/** Human label for Cognito's UserStatus enum. */
const describeStatus = (profile: UserProfile): { label: string; tone: 'good' | 'bad' | 'warn' | 'neutral' } => {
    if (!profile.enabled) return { label: 'Disabled', tone: 'bad' }
    switch (profile.status) {
        case 'CONFIRMED':
            return { label: 'Active', tone: 'good' }
        case 'FORCE_CHANGE_PASSWORD':
            return { label: 'Password change required', tone: 'warn' }
        case 'RESET_REQUIRED':
            return { label: 'Password reset required', tone: 'warn' }
        case 'UNCONFIRMED':
            return { label: 'Unconfirmed', tone: 'warn' }
        default:
            return { label: profile.status || 'Unknown', tone: 'neutral' }
    }
}

/**
 * Account → Your profile. Identity + account facts on the left; on the right,
 * tabbed sections for personal details / email, security (password, 2FA,
 * sessions) and notification preferences. Writes go to Cognito and are
 * mirrored onto `dashboard_users` by the API.
 *
 * The active tab lives in the URL hash (`/profile#security`) so emails and
 * the sidebar can deep-link to it.
 */
export const ProfileView = () => {
    const authUser = useAuthUserData()
    const location = useLocation()
    const navigate = useNavigate()

    const [profile, setProfile] = useState<UserProfile | null>(null)
    const [loading, setLoading] = useState(true)
    const [loadError, setLoadError] = useState<string | null>(null)

    const tab = tabFromHash(location.hash)
    const setTab = (next: ProfileTab) => navigate({ hash: next === 'profile' ? '' : `#${next}` }, { replace: true })

    const loadProfile = useCallback(async () => {
        setLoading(true)
        setLoadError(null)
        try {
            const response = await api({ url: '/profile', data: {} })
            const data: UserProfile | undefined = response.data?.profile
            if (!response.data?.success || !data) {
                throw new Error(response.data?.error || 'Profile unavailable')
            }
            setProfile(data)
        } catch (error: any) {
            setLoadError(apiErrorMessage(error, 'Could not load your profile.'))
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        void loadProfile()
    }, [loadProfile])

    const patchProfile = useCallback((patch: Partial<UserProfile>) => {
        setProfile((prev) => (prev ? { ...prev, ...patch } : prev))
    }, [])

    const initial = (profile?.name || authUser?.name || profile?.email || '?').trim().charAt(0)
    const status = profile ? describeStatus(profile) : null
    const extraOrgs = profile ? profile.organisations.filter((org) => org !== profile.organisation) : []

    return (
        <Page>
            <PageHeader
                eyebrow="Account"
                title="Your profile"
                intro="Review your account, keep your contact details current and manage how you sign in. Changes apply to your login straight away."
                actions={
                    <Button size="sm" icon={<FaSyncAlt />} onClick={() => void loadProfile()} disabled={loading}>
                        Refresh
                    </Button>
                }
            />

            {loading && !profile ? (
                <EmptyState tone="loading" title="Loading your profile…" />
            ) : loadError && !profile ? (
                <EmptyState
                    tone="error"
                    title="Couldn't load your profile"
                    description={loadError}
                    action={<Button onClick={() => void loadProfile()}>Try again</Button>}
                />
            ) : profile ? (
                <div className={s.layout}>
                    <div className={s.column}>
                        <Card>
                            <div className={s.identity}>
                                <div className={s.identityAvatar} aria-hidden="true">
                                    {initial}
                                </div>
                                <div>
                                    <h2 className={s.identityName}>{profile.name || 'Unnamed user'}</h2>
                                    <p className={s.identityEmail}>
                                        <BreakableEmail email={profile.email} />
                                    </p>
                                </div>
                                <div className={s.identityPills}>
                                    {profile.organisation && <StatusPill tone="accent">{profile.organisation}</StatusPill>}
                                    {profile.admin && <StatusPill tone="info">Admin</StatusPill>}
                                    {status && (
                                        <StatusPill tone={status.tone} dot>
                                            {status.label}
                                        </StatusPill>
                                    )}
                                    {profile.mfa.enabled && (
                                        <StatusPill tone="good" size="sm">
                                            <FaShieldAlt aria-hidden="true" /> 2FA on
                                        </StatusPill>
                                    )}
                                </div>
                            </div>
                        </Card>

                        <Card title="Account details" subtitle="Managed by your administrator.">
                            <KeyValueList
                                stacked
                                items={[
                                    { label: 'Email', value: <BreakableEmail email={profile.email} /> },
                                    { label: 'Email verified', value: profile.emailVerified ? 'Yes' : 'No' },
                                    { label: 'Primary organisation', value: profile.organisation },
                                    {
                                        label: 'Additional access',
                                        value: extraOrgs.length ? (
                                            <span className={s.orgList}>
                                                {extraOrgs.map((org) => (
                                                    <StatusPill key={org} size="sm">
                                                        {org}
                                                    </StatusPill>
                                                ))}
                                            </span>
                                        ) : (
                                            ''
                                        ),
                                    },
                                    { label: 'Role', value: profile.admin ? 'Administrator' : 'User' },
                                    { label: 'Two-factor authentication', value: profile.mfa.enabled ? 'On (authenticator app)' : 'Off' },
                                    { label: 'Member since', value: formatDate(profile.createdAt) },
                                    { label: 'Last sign-in', value: formatDateTime(profile.lastLogin) },
                                    { label: 'Password last changed', value: formatDateTime(profile.passwordChangedAt) },
                                    { label: 'User ID', value: profile.cognitoId, mono: true },
                                ]}
                                emptyText="—"
                            />
                        </Card>
                    </div>

                    <div className={s.column}>
                        <Tabs<ProfileTab>
                            aria-label="Profile sections"
                            noMargin
                            value={tab}
                            onChange={setTab}
                            items={[
                                { value: 'profile', label: 'Profile', icon: <FaUser /> },
                                { value: 'security', label: 'Security', icon: <FaShieldAlt />, dot: !profile.mfa.enabled },
                                { value: 'notifications', label: 'Notifications', icon: <FaBell /> },
                            ]}
                        />

                        {tab === 'profile' && (
                            <>
                                {/* Keyed on the saved values so a Refresh re-seeds the form. */}
                                <PersonalDetailsCard key={`${profile.name}|${profile.phone}`} profile={profile} onChange={patchProfile} />
                                <EmailCard profile={profile} onChange={patchProfile} />
                            </>
                        )}

                        {tab === 'security' && (
                            <>
                                <TwoFactorCard profile={profile} onChange={patchProfile} />
                                <PasswordCard profile={profile} onChange={patchProfile} />
                                <SessionsCard />
                            </>
                        )}

                        {tab === 'notifications' && <NotificationsCard profile={profile} onChange={patchProfile} />}
                    </div>
                </div>
            ) : null}
        </Page>
    )
}

export default ProfileView
